/**
 * BUTTON COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass morphing on interaction
 * - Energize effect - glows from ACTUAL touch point (per WWDC25)
 * - Adaptive tinting respecting glass physics
 * - Specular highlights with pearlescent shimmer
 * - Fluid spring animations
 * - Gel-like compression on press
 * - Touch ripple spreading from contact point
 */

import { ButtonHTMLAttributes, ReactNode, useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'glass' | 'ghost' | 'destructive' | 'tinted'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  isLoading?: boolean
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  tint?: 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'pink' | 'teal'
  children: ReactNode
}

// Touch point state for energize effect
interface TouchPoint {
  id: number
  x: number
  y: number
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  icon,
  iconPosition = 'left',
  tint = 'blue',
  children,
  className,
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [touchPoints, setTouchPoints] = useState<TouchPoint[]>([])
  const touchIdCounter = useRef(0)

  // Handle energize effect - glow from actual touch point
  const handleInteraction = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) return

    const button = buttonRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    let clientX: number, clientY: number

    if ('touches' in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    const x = clientX - rect.left
    const y = clientY - rect.top
    const id = touchIdCounter.current++

    setTouchPoints(prev => [...prev, { id, x, y }])

    // Remove touch point after animation completes
    setTimeout(() => {
      setTouchPoints(prev => prev.filter(tp => tp.id !== id))
    }, 600)
  }, [disabled, isLoading])

  // Combine click handlers
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    handleInteraction(e)
    onClick?.(e)
  }, [handleInteraction, onClick])

  // Base styles - shared across all variants
  const baseStyles = `
    inline-flex items-center justify-center gap-2.5 font-semibold rounded-xl tracking-tight
    transition-all duration-fast ease-liquid-out
    focus:outline-none focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-blue/50 focus-visible:ring-offset-2
    active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed
    disabled:pointer-events-none relative overflow-hidden
    select-none cursor-pointer
  `

  // Variant styles
  const variants = {
    // Primary - Solid gradient button
    primary: `
      bg-gradient-to-b from-blue to-blue-hover
      dark:from-blue-dark dark:to-blue
      text-white shadow-liquid-md dark:shadow-dark-liquid-md
      hover:shadow-liquid-lg dark:hover:shadow-dark-liquid-lg
      hover:brightness-110
      active:shadow-liquid-sm dark:active:shadow-dark-liquid-sm
      before:absolute before:inset-0 before:rounded-xl
      before:bg-gradient-to-b before:from-white/25 before:to-transparent
      before:opacity-80
    `,

    // Secondary - Glass with tint
    secondary: `
      liquid-material-regular liquid-lens
      text-blue dark:text-blue-dark
      hover:bg-blue-glass dark:hover:bg-blue-glass
      shadow-glass dark:shadow-dark-glass
      hover:shadow-glass-lg dark:hover:shadow-dark-glass-lg
    `,

    // Glass - Neutral glass button
    glass: `
      liquid-material-regular liquid-lens
      text-light-text-primary dark:text-dark-text-primary
      shadow-glass dark:shadow-dark-glass
      hover:shadow-glass-lg dark:hover:shadow-dark-glass-lg
    `,

    // Ghost - Minimal, no background
    ghost: `
      bg-transparent text-light-text-primary dark:text-dark-text-primary
      hover:bg-light-bg-secondary/60 dark:hover:bg-dark-bg-secondary/60
      active:bg-light-bg-tertiary dark:active:bg-dark-bg-tertiary
    `,

    // Destructive - Red gradient
    destructive: `
      bg-gradient-to-b from-red to-red-hover
      dark:from-red-dark dark:to-red
      text-white shadow-liquid-md dark:shadow-dark-liquid-md
      hover:shadow-liquid-lg dark:hover:shadow-dark-liquid-lg
      hover:brightness-110
      active:shadow-liquid-sm dark:active:shadow-dark-liquid-sm
      before:absolute before:inset-0 before:rounded-xl
      before:bg-gradient-to-b before:from-white/25 before:to-transparent
      before:opacity-80
    `,

    // Tinted - Glass with color tint (uses tint prop)
    tinted: `
      liquid-material-regular liquid-lens
      shadow-glass dark:shadow-dark-glass
      hover:shadow-glass-lg dark:hover:shadow-dark-glass-lg
    `,
  }

  // Tint colors for the tinted variant
  const tintColors = {
    blue: 'text-blue dark:text-blue-dark liquid-tint-blue',
    green: 'text-green dark:text-green-dark liquid-tint-green',
    red: 'text-red dark:text-red-dark liquid-tint-red',
    orange: 'text-orange dark:text-orange-dark liquid-tint-orange',
    purple: 'text-purple dark:text-purple-dark liquid-tint-purple',
    pink: 'text-pink dark:text-pink-dark liquid-tint-pink',
    teal: 'text-teal dark:text-teal-dark liquid-tint-teal',
  }

  // Size styles
  const sizes = {
    sm: 'px-4 py-2 text-subheadline gap-2',
    md: 'px-5 py-2.5 text-body gap-2.5',
    lg: 'px-6 py-3.5 text-headline gap-3'
  }

  // Icon sizes
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-5 h-5'
  }

  // Determine glow color based on variant
  const glowColors = {
    primary: 'rgba(255,255,255,0.5)',
    secondary: 'rgba(0,122,255,0.3)',
    glass: 'rgba(255,255,255,0.4)',
    ghost: 'rgba(255,255,255,0.2)',
    destructive: 'rgba(255,255,255,0.5)',
    tinted: 'rgba(255,255,255,0.35)',
  }

  return (
    <button
      ref={buttonRef}
      className={cn(
        baseStyles,
        variants[variant],
        variant === 'tinted' && tintColors[tint],
        sizes[size],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled || isLoading}
      onClick={handleClick}
      onTouchStart={handleInteraction}
      {...props}
    >
      {/* Specular highlight sweep - pearlescent shimmer on hover */}
      <span
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-base pointer-events-none"
        style={{
          background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 45%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.15) 55%, transparent 60%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s ease-in-out infinite',
        }}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-inherit rounded-xl z-10">
          <div className="relative w-5 h-5">
            <div className="absolute inset-0 border-2 border-current opacity-20 rounded-full" />
            <div className="absolute inset-0 border-2 border-current border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      )}

      {/* Button content */}
      <span className={cn(
        'inline-flex items-center gap-2.5 transition-opacity duration-fast relative z-[1]',
        isLoading && 'opacity-0'
      )}>
        {icon && iconPosition === 'left' && (
          <span className={cn('inline-flex shrink-0', iconSizes[size])}>{icon}</span>
        )}
        <span className="tracking-tight">{children}</span>
        {icon && iconPosition === 'right' && (
          <span className={cn('inline-flex shrink-0', iconSizes[size])}>{icon}</span>
        )}
      </span>

      {/* Energize glow effect - spreads from ACTUAL touch point */}
      <AnimatePresence>
        {touchPoints.map(({ id, x, y }) => (
          <motion.span
            key={id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: x,
              top: y,
              width: 0,
              height: 0,
              transform: 'translate(-50%, -50%)',
              background: `radial-gradient(circle, ${glowColors[variant]} 0%, transparent 70%)`,
            }}
            initial={{ width: 0, height: 0, opacity: 0.8 }}
            animate={{ width: 300, height: 300, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
          />
        ))}
      </AnimatePresence>

      {/* Top edge highlight for depth */}
      <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none rounded-t-xl" />
    </button>
  )
}

/**
 * IconButton - Circular glass button for icons only
 * Features touch point energize effect like main Button
 */
interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'glass' | 'ghost' | 'primary' | 'destructive'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  children: ReactNode
}

export function IconButton({
  variant = 'glass',
  size = 'md',
  isLoading = false,
  children,
  className,
  disabled,
  onClick,
  ...props
}: IconButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [touchPoints, setTouchPoints] = useState<TouchPoint[]>([])
  const touchIdCounter = useRef(0)

  // Handle energize effect - glow from actual touch point
  const handleInteraction = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) return

    const button = buttonRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    let clientX: number, clientY: number

    if ('touches' in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    const x = clientX - rect.left
    const y = clientY - rect.top
    const id = touchIdCounter.current++

    setTouchPoints(prev => [...prev, { id, x, y }])

    setTimeout(() => {
      setTouchPoints(prev => prev.filter(tp => tp.id !== id))
    }, 500)
  }, [disabled, isLoading])

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    handleInteraction(e)
    onClick?.(e)
  }, [handleInteraction, onClick])

  const baseStyles = `
    inline-flex items-center justify-center rounded-full
    transition-all duration-fast ease-liquid-out
    focus:outline-none focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-blue/50 focus-visible:ring-offset-2
    active:scale-[0.95] disabled:opacity-50 disabled:cursor-not-allowed
    disabled:pointer-events-none relative overflow-hidden
    select-none cursor-pointer
  `

  const variants = {
    glass: `
      liquid-material-regular liquid-lens
      text-light-text-primary dark:text-dark-text-primary
      shadow-glass-sm dark:shadow-dark-glass-sm
      hover:shadow-glass dark:hover:shadow-dark-glass
      hover:scale-105
    `,
    ghost: `
      bg-transparent text-light-text-secondary dark:text-dark-text-secondary
      hover:bg-light-bg-secondary/60 dark:hover:bg-dark-bg-secondary/60
      hover:text-light-text-primary dark:hover:text-dark-text-primary
    `,
    primary: `
      bg-gradient-to-b from-blue to-blue-hover
      dark:from-blue-dark dark:to-blue
      text-white shadow-liquid-sm dark:shadow-dark-liquid-sm
      hover:shadow-liquid-md dark:hover:shadow-dark-liquid-md
      hover:brightness-110
    `,
    destructive: `
      bg-gradient-to-b from-red to-red-hover
      dark:from-red-dark dark:to-red
      text-white shadow-liquid-sm dark:shadow-dark-liquid-sm
      hover:shadow-liquid-md dark:hover:shadow-dark-liquid-md
      hover:brightness-110
    `,
  }

  const sizes = {
    sm: 'w-8 h-8 p-1.5',
    md: 'w-10 h-10 p-2',
    lg: 'w-12 h-12 p-2.5'
  }

  const glowColors = {
    glass: 'rgba(255,255,255,0.4)',
    ghost: 'rgba(255,255,255,0.2)',
    primary: 'rgba(255,255,255,0.5)',
    destructive: 'rgba(255,255,255,0.5)',
  }

  return (
    <button
      ref={buttonRef}
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || isLoading}
      onClick={handleClick}
      onTouchStart={handleInteraction}
      {...props}
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <span className="relative z-[1]">{children}</span>
      )}

      {/* Energize glow effect */}
      <AnimatePresence>
        {touchPoints.map(({ id, x, y }) => (
          <motion.span
            key={id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)',
              background: `radial-gradient(circle, ${glowColors[variant]} 0%, transparent 70%)`,
            }}
            initial={{ width: 0, height: 0, opacity: 0.8 }}
            animate={{ width: 120, height: 120, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          />
        ))}
      </AnimatePresence>
    </button>
  )
}
